import { BaseGQLService } from '../../../common/services/BaseGQLService';
import { FilterOperator, SorterSet } from '../../../common/types/gql-types';
import { CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { ProjectMapper } from '../mappers/ProjectMapper';
import { Project } from '../models/Project';

export const GET_PROJECTS_QUERY = `
query (
  $first: Int,
  $last: Int,
  $after: String,
  $before: String,
  $filters: ProjectsFilter,
  $sorters: [ProjectsSorter],
) {
  viewer {
    organization {
      projects(
        first: $first,
        after: $after,
        last: $last,
        before: $before,
        filters: $filters,
        sorters: $sorters,
      ) {
        totalCount
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
        edges {
          node {
            id
            name
            description
            maxItemCount
            lastAnalysis
            defaultProfile {
              id
              name
              type
            }
            vulnerabilityStatistics {
              severity
              amount
            }
          }
        }
      }
    }
  }
}
`;

export class ProjectRepository extends BaseGQLService {
  private readonly mapper = new ProjectMapper();

  async getList(cursor: CursorInfo, filters: FilterOperator | null, sorters: SorterSet[]): Promise<PaginationResult<Project>> {
    return this.query(
      GET_PROJECTS_QUERY,
      {
        ...this.mapCursorInfo(cursor),
        filters,
        sorters: this.getSorters(sorters),
      },
      (data) => {
        const { edges, pageInfo, totalCount } = data.viewer.organization.projects;
        return {
          data: edges.map((p: any) => this.mapper.mapToDomain(p.node)),
          totalCount,
          pageInfo,
        };
      }
    );
  }

  /**
   * Projects are returned by the API sorted by ascending ID (oldest-newest).
   * Unless explicit sorters are requested, sort projects from newest to oldest.
   *
   * @param sorters the requested sorters
   * @returns the requested sorters if any, otherwise the default sorter
   */
  private getSorters(sorters: SorterSet[]): SorterSet[] {
    if (sorters.length > 0) {
      return sorters;
    }
    return [{ field: 'id', order: 'DESC' }];
  }
}
